/**
 * OpenAPI CollectionQuery（POST /liyuan-market/collection/page）
 * 合法字段：active, category, closed, featured, id, question, sortType, tagId, groupBySeries
 * sortType 文档枚举：1 = 成交量从高到低
 * groupBySeries：首页「全部 / Crypto 分类 / 加密分钟盘」与探索推荐展示态去重（币种×周期堆叠）；
 * 其它 API 分类 tab、搜索 question 不传；默认不传=原始分页（MM 等不受影响）
 */
import {
  FEED_CRYPTO_MINUTE_CATEGORY_KEY,
  isCryptoFeedCategoryKey,
} from '@/domain/market/feed-list-category.js';

export const COLLECTION_SORT_TYPE = {
  VOLUME_DESC: 1,
};

/** 是否在 Feed UI 展示排序控件（newest/ending 等 API 未就绪前保持 false） */
export const FEED_SORT_UI_ENABLED = false;

/**
 * 首页 Feed 排序选项（UI sortId → OpenAPI sortType）
 * 仅包含后端已支持的项；newest/ending 待 OpenAPI 枚举后再加
 */
export const FEED_SORT_OPTIONS = [
  { id: 'trending', i18nKey: 'markets.sortTrending', sortType: undefined },
  {
    id: 'volume',
    i18nKey: 'markets.sortHighestVolume',
    sortType: COLLECTION_SORT_TYPE.VOLUME_DESC,
  },
];

/** 当前应对用户展示的排序项（由 FEED_SORT_UI_ENABLED 门控） */
export const FEED_SORT_UI_OPTIONS = FEED_SORT_UI_ENABLED ? FEED_SORT_OPTIONS : [];

export const PAGE_SIZE = 32;

/** 探索页固定 query（成交量降序；与首页同传 groupBySeries，按币种×周期堆叠） */
export const EXPLORE_COLLECTION_QUERY = {
  sortType: COLLECTION_SORT_TYPE.VOLUME_DESC,
  groupBySeries: true,
};

/**
 * 将 UI sortId 映射为 OpenAPI sortType；无文档枚举时不传
 * @param {string} sortId
 * @returns {number|undefined}
 */
export function resolveSortType(sortId) {
  return FEED_SORT_OPTIONS.find((o) => o.id === sortId)?.sortType;
}

/** 仅当选项映射到 OpenAPI sortType 时才发送（trending 走后端默认序） */
export function shouldSendCollectionSortType(_platform, sortId) {
  return resolveSortType(sortId) != null;
}

/**
 * 构建 CollectionQuery 请求体（仅包含 OpenAPI 定义字段）
 * @param {object} state marketFeed store 快照
 * @param {string} [state.primaryCategory]
 * @param {string} [state.appliedQuestion]
 * @param {string} [state.sortId]
 * @param {'h5'|'pc'} [state.lastFetchPlatform]
 */
export function buildCollectionQuery(state) {
  const platform = state.lastFetchPlatform === 'h5' ? 'h5' : 'pc';
  const query = {};

  const sortType = resolveSortType(state.sortId);
  if (shouldSendCollectionSortType(platform, state.sortId) && sortType != null) {
    query.sortType = sortType;
  }

  const category = String(state.primaryCategory ?? '').trim();
  const hasAPICategory =
    Boolean(category) && category !== 'all' && category !== FEED_CRYPTO_MINUTE_CATEGORY_KEY;
  if (hasAPICategory) {
    query.category = category;
  }

  const question = String(state.appliedQuestion ?? '').trim();
  if (question) {
    query.question = question;
  }

  // 「全部 / 加密分钟盘 / Crypto 分类」无搜索：后端系列去重；其它分类与搜索保持原始分页
  // （Politics/Sports 等不扫全表 dedupe，避免 silent 刷新路径与负载变化）
  const wantsSeriesGroup = !hasAPICategory || isCryptoFeedCategoryKey(category);
  if (wantsSeriesGroup && !question) {
    query.groupBySeries = true;
  }

  return query;
}
